import React, { useContext, useEffect } from 'react';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import t from 'react-translate';
import { AngularContext } from 'react-app';

import { useSelector } from 'react-redux';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { Institution } from 'redux/schemas/models/institution';
import { useAppDispatch } from 'redux/store';
import { enableAccredibleSettings } from 'redux/actions/digital-credential';
import { wrapThunkAction } from 'redux/utils';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { AccredibleSettingsPayload } from 'redux/schemas/models/credly';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import NvTextInput from 'shared/components/inputs/nv-text-input';

import {
  doubleSpacing,
  tripleSpacing,
  quarterSpacing,
  cardIconSize,
} from 'styles/global_defaults/scaffolding';
import { config } from '@config/pendo.config.json';

type Props = {
  show: boolean;
  onClose: () => void;
};

type FormData = {
  apiKey: string;
  domainUrl: string;
};

const styles = css`
  .message {
    margin-bottom: ${5 * quarterSpacing}px;
  }
`;

const modalStyles = css`
  .bs4-modal-body {
    padding: ${doubleSpacing}px ${tripleSpacing}px ${cardIconSize}px ${tripleSpacing}px;
  }
`;

const validationSchema = (
  errorMessages: { required: string, http: string },
) => yup.object().shape({
  apiKey: yup.string().trim().required(errorMessages.required),
  domainUrl: yup
    .string()
    .trim()
    .required(errorMessages.required)
    .test('is-http', errorMessages.http, (value) => {
      if (!value) return false;
      const lower = value.toLowerCase();
      return lower.startsWith('http://') || lower.startsWith('https://');
    }),
});

const AccredibleSettings = ({ show, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const institution: Institution = useSelector(state => getCurrentInstitution(state));
  const { injectServices } = useContext(AngularContext);
  const [InstitutionsManager] = injectServices(['InstitutionsManager']);
  const hasAccredibleIntegration = institution?.isAccredibleEnabled ?? false;
  const accredibleConfig = institution?.accredibleConfig;

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema({
      required: t.VALIDATION.REQUIRED(),
      http: t.VALIDATION.HTTP(),
    })),
    defaultValues: {
      apiKey: accredibleConfig?.apiKey ?? '',
      domainUrl: accredibleConfig?.domainUrl ?? '',
    },
  });

  const {
    formState: { isValid, isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = formMethods;

  useEffect(() => {
    if (show) {
      reset({
        apiKey: accredibleConfig?.apiKey ?? '',
        domainUrl: accredibleConfig?.domainUrl ?? '',
      });
    }
  }, [show, accredibleConfig, reset]);

  const handleEnable = (data: FormData): Promise<void> => wrapThunkAction<AccredibleSettingsPayload>(
    dispatch(
      enableAccredibleSettings({
        institutionId: institution.id,
        apiKey: data.apiKey,
        domainUrl: data.domainUrl,
      }),
    ),
  )
    .then(response => {
      dispatch(
        addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS_BANG(),
          message: hasAccredibleIntegration
            ? t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.UPDATE_MESSAGE()
            : t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.SUCCESS_MESSAGE(),
        }),
      );

      InstitutionsManager.institution.updateFromReact({
        ...response.payload.institutionList,
      });

      onClose();
    })
    .catch(() => {
      dispatch(
        addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.INSTITUTIONS.ADVANCED_SETTINGS.CREDLY.SETTINGS_MODAL.ERROR_TITLE(),
          message: t.INSTITUTIONS.ADVANCED_SETTINGS.CREDLY.SETTINGS_MODAL.ERROR_MESSAGE(),
        }),
      );
    });

  const getButtonText = () => {
    if (isSubmitting) {
      return hasAccredibleIntegration
        ? t.FORM.UPDATING()
        : t.FORM.ENABLING();
    }
    return hasAccredibleIntegration
      ? t.FORM.UPDATE()
      : t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.ENABLE_ACCREDIBLE_INTEGRATION();
  };

  return (
    <React.Fragment>
      <NvModal
        show={show}
        onClose={onClose}
        type={ModalType.FIXED}
        height='auto'
        modalStyles={modalStyles}
        header={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.TITLE()}
        data-qa={config.pendo.digitalCredential.accredibleIntegration.modal}
        body={(
          <FormProvider {...formMethods}>
            <form css={styles} onSubmit={handleSubmit(handleEnable)}>
              <div className='message text-body'>
                {t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.DESCRIPTION()}
              </div>

              <NvTextInput
                withForm
                required
                autoComplete='off'
                name='apiKey'
                withLabel
                labelAlwaysShown
                labelClassName='text-gray-3'
                placeholder={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.API_TOKEN()}
                ariaLabel={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.API_TOKEN()}
                data-qa={config.pendo.digitalCredential.accredibleIntegration.apiToken}
              />

              <NvTextInput
                withForm
                required
                autoComplete='off'
                name='domainUrl'
                withLabel
                labelAlwaysShown
                labelClassName='text-gray-3'
                className='mt-5'
                placeholder={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.DOMAIN()}
                ariaLabel={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.DOMAIN()}
                data-qa={config.pendo.digitalCredential.accredibleIntegration.domain}
              />

              <div className='mt-8 mb-6 d-flex justify-content-center'>
                <Button
                  variant='secondary'
                  onClick={onClose}
                  data-qa={config.pendo.digitalCredential.accredibleIntegration.cancel}
                  className='mr-2'
                >
                  {t.FORM.CANCEL()}
                </Button>

                <Button
                  type='submit'
                  variant='primary'
                  className='ml-2'
                  disabled={!isValid || isSubmitting || !isDirty}
                  data-qa={!hasAccredibleIntegration
                    ? config.pendo.digitalCredential.accredibleIntegration.enable
                    : config.pendo.digitalCredential.accredibleIntegration.update}
                >
                  {getButtonText()}
                </Button>
              </div>
            </form>
          </FormProvider>
        )}
      />
    </React.Fragment>
  );
};

export default AccredibleSettings;
